import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Paper } from '@mui/material';

/** Material UI Icons. */
import PaidIcon from '@mui/icons-material/Paid';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import DynamicToggleButton from 'shared/ui/ToggleButton';
import { MainDatagrid } from 'shared/ui/Datagrid';
import SocketAlert from 'shared/ui/SocketAlert';
import { PageHeader } from 'shared/ui/HeaderIcon';
import { readSocketData, updateSocketData } from 'shared/lib/fetchSocket';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';

import { getColumn } from './lib/AccountingDef';
import { createLatestTabRead } from './lib/latestTabRead';
import { setAccountingData, updateAccountingData } from 'shared/store/slices/accountingUpdateSlice';

/** Status changes arriving within this window share one re-read (e.g. a pasted column). */
const STATUS_REFRESH_DELAY_MS = 300;

const TAB_LIST = [
    {
        content: 'IN PROGRESS',
        value: 0,
    },
    {
        content: 'COMPLETE',
        value: 1,
    },
];

export default function Accounting() {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State for showing the connection status of the WebSocket. */
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);
    const accountingData = useSelector((state) => state.accountingList);

    /** State for the selected value of toggle buttons. */
    const [tabValue, setTabValue] = useState(0);
    const [accountingRows, setAccountingRows] = useState([]);
    /** State to represent whether the acoounting information is currently being loaded. */
    const [loading, setLoading] = useState(false);

    /** Warn the user when a pasted value doesn't match a select column's options. */
    const handleInvalidOption = useInvalidOptionWarning();

    /** Named website title. */
    useEffect(() => {
        document.title = 'Accounting | Operation System';
    }, []);

    /** Reads a tab's shipments; a response that a newer read of the same tab already replaced is dropped. */
    const latestTabRead = useRef(createLatestTabRead()).current;
    const readTab = useCallback(
        (tab, onDone) => {
            const ticket = latestTabRead.start(tab);
            readSocketData(dispatch, tab, 'row', 'il_accounting', '', null, (receivedData) => {
                if (latestTabRead.isLatest(tab, ticket)) {
                    dispatch(setAccountingData({ tabValue: tab, data: receivedData.data }));
                }
                onDone?.();
            });
        },
        [dispatch, latestTabRead],
    );

    /** useEffect to load accounting data when navigating to the page. */
    useEffect(() => {
        if (!socketConnected) return;
        setLoading(true);
        readTab(tabValue, () => setLoading(false));
    }, [socketConnected, tabValue, readTab]);

    /**
     * When anyone changes a Collect Charge status, re-read the open tab so the shipment moves to the
     * right tab, drops off (Confirmed), or appears (newly Payment Requested). A burst becomes one read.
     */
    const refreshSeq = accountingData.refreshSeq;
    const readRefreshSeq = useRef(refreshSeq);
    useEffect(() => {
        if (!socketConnected || readRefreshSeq.current === refreshSeq) return;
        const timer = setTimeout(() => {
            readRefreshSeq.current = refreshSeq;
            readTab(tabValue);
        }, STATUS_REFRESH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [refreshSeq, socketConnected, tabValue, readTab]);

    /** Sync DataGrid rows with the current tab's accounting state. */
    useEffect(() => {
        setAccountingRows(accountingData[tabValue] ?? []);
    }, [accountingData, tabValue]);

    /** Toggle group button handler. */
    const handleTabChange = (event, newValue) => {
        if (newValue === null) return;
        setTabValue(newValue);
    };

    /** Handler to update a row in accounting table. */
    const handleProcessRowUpdate = (newRow, oldRow) => {
        // Date-only fields: keep local calendar day (avoid UTC day shift via toISOString).
        const formatDateToString = (date) => formatDateToDateString(date);
        const changedFields = Object.keys(newRow).reduce((acc, key) => {
            if (JSON.stringify(newRow[key]) !== JSON.stringify(oldRow[key])) {
                acc[key] = newRow[key];
            }
            return acc;
        }, {});
        if (Object.keys(changedFields).length === 0) return oldRow;

        const key = Object.keys(changedFields)[0];
        let value = changedFields[key];

        const requiredFields = ['freight', 'paid_with'];
        const invalidFields = requiredFields.filter((field) => {
            const value = newRow[field];
            return value === null || value === undefined || value === '';
        });

        if (invalidFields.length > 0) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please select a option.',
                    severity: 'warning',
                }),
            );
            return oldRow; // Return old row to prevent update
        }

        const emitObj = {
            id: newRow.id,
            fieldName: key,
            fieldValue: formatDateToString(value) || null,
        };

        // Optimistic update: reflect the new value right away instead of waiting on the server ack,
        // then fire the request in the background and roll back only if it fails.
        dispatch(updateAccountingData({ tabValue, id: newRow.id, key, value: emitObj.fieldValue }));

        updateSocketData(
            dispatch,
            tabValue,
            'row',
            'il_accounting',
            'updateAccountingData',
            emitObj,
            (response) => {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: response.message,
                        severity: 'success',
                    }),
                );
            },
            () => {
                dispatch(updateAccountingData({ tabValue, id: newRow.id, key, value: oldRow[key] }));
            },
        );

        return newRow;
    };

    /** Handles errors that occur during row updates in the DataGrid. */
    const handleProcessRowUpdateError = (error) => {
        console.error(error);
    };

    return (
        <Paper sx={customized_style.itemPaper}>
            <SocketAlert />
            <PageHeader title={'ACCOUNTING'} icon={PaidIcon} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <DynamicToggleButton
                    data={TAB_LIST}
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{ width: '6rem' }}
                />
            </Box>
            <Box>
                <MainDatagrid
                    rows={accountingRows}
                    cols={getColumn(handleInvalidOption)}
                    getRowHeight={() => 'auto'}
                    loading={loading}
                    processRowUpdate={handleProcessRowUpdate}
                    showToolbar="GridToolbar"
                    id_status={false}
                    additional={{
                        editMode: 'cell',
                        onProcessRowUpdateError: handleProcessRowUpdateError,
                        disableDensitySelector: true,
                        disableAggregation: true,
                        cellSelection: true,
                        disableRowSelectionOnClick: true,
                        slotProps: {
                            toolbar: {
                                showQuickFilter: true,
                                printOptions: { disableToolbarButton: true },
                                excelOptions: {
                                    disableToolbarButton: true,
                                },
                                csvOptions: {
                                    allColumns: true,
                                    hiddenColumns: ['invoice_link'],
                                    exportFileName: 'Accounting',
                                },
                            },
                        },
                    }}
                    sx={{
                        height: '80vh',
                        textAlign: 'center',
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .custom-editable-header': {
                            textDecoration: 'underline',
                        },
                    }}
                />
            </Box>
            <CustomSnackbar />
        </Paper>
    );
}
